/**
 * Utility functions for handling image URLs consistently across the application
 */

/**
 * Converts any image URL to a proper absolute URL for social sharing
 * @param {string} imageUrl - The original image URL (can be relative or absolute)
 * @param {string} fallbackUrl - Fallback URL if image is not provided
 * @returns {string} Properly formatted absolute image URL
 */
export const getSocialImageUrl = (imageUrl, fallbackUrl = null) => {
  const defaultFallback = 'https://jobsopportunity.in/images/logo-dark.png';
  const finalFallback = fallbackUrl || defaultFallback;
  
  // Debug logging
  console.log('getSocialImageUrl Debug:', {
    imageUrl,
    imageUrlType: typeof imageUrl,
    fallbackUrl,
    finalFallback
  });
  
  // If no image provided, use fallback
  if (!imageUrl || typeof imageUrl !== 'string') {
    console.log('Using fallback image:', finalFallback);
    return finalFallback;
  }
  
  // If it's already a full URL, return as is
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    console.log('Using full URL:', imageUrl);
    return imageUrl;
  }
  
  // If it's a relative path, make it absolute
  if (imageUrl.startsWith('/')) {
    const absoluteUrl = `https://jobsopportunity.in${imageUrl}`;
    console.log('Converting relative path to absolute:', absoluteUrl);
    return absoluteUrl;
  }
  
  // If it's a relative path without leading slash
  const absoluteUrl = `https://jobsopportunity.in/${imageUrl}`;
  console.log('Converting relative path to absolute:', absoluteUrl);
  return absoluteUrl;
};

/**
 * Gets the appropriate fallback image based on content type
 * @param {string} type - Content type ('blog', 'job', 'company', etc.)
 * @returns {string} Fallback image URL
 */
export const getFallbackImage = (type = 'default') => {
  const fallbacks = {
    blog: 'https://jobsopportunity.in/images/blog-placeholder.jpg',
    job: 'https://jobsopportunity.in/images/company-placeholder.png',
    company: 'https://jobsopportunity.in/images/company-placeholder.png',
    default: 'https://jobsopportunity.in/images/logo-dark.png'
  };
  
  const fallback = fallbacks[type] || fallbacks.default;
  console.log('getFallbackImage:', { type, fallback });
  return fallback;
};

/**
 * Validates if an image URL is accessible
 * @param {string} imageUrl - The image URL to validate
 * @returns {Promise<boolean>} True if image is accessible, false otherwise
 */
export const validateImageUrl = async (imageUrl) => {
  if (!imageUrl || typeof imageUrl !== 'string') return false;
  
  try {
    const response = await fetch(imageUrl, { method: 'HEAD' });
    return response.ok;
  } catch (error) {
    console.warn('Image validation failed:', error);
    return false;
  }
};

/**
 * Gets optimized image URL for social sharing with proper dimensions
 * @param {string} imageUrl - Original image URL
 * @param {string} type - Content type for fallback selection
 * @returns {string} Optimized image URL for social sharing
 */
export const getOptimizedSocialImage = (imageUrl, type = 'default') => {
  console.log('getOptimizedSocialImage Debug:', { imageUrl, type });
  const fallback = getFallbackImage(type);
  return getSocialImageUrl(imageUrl, fallback);
};

/**
 * Enhanced function to get the best available image for Open Graph
 * @param {Object} content - Content object (blog or job)
 * @param {string} type - Content type ('blog' or 'job')
 * @returns {string} Best available image URL
 */
export const getBestOpenGraphImage = (content, type = 'blog') => {
  console.log('getBestOpenGraphImage Debug:', { content, type });
  
  if (!content) {
    return getFallbackImage(type);
  }
  
  let imageUrl = null;
  
  if (type === 'blog') {
    // For blogs, check multiple image fields in order of preference
    const imageFields = [
      'heroImage',
      'featuredImage', 
      'coverImage',
      'image',
      'thumbnail',
      'imageUrl'
    ];
    
    for (const field of imageFields) {
      if (content[field]) {
        // Handle object structure (e.g., { url: '...' })
        if (typeof content[field] === 'object' && content[field]?.url) {
          imageUrl = content[field].url;
          break;
        }
        // Handle string URL
        else if (typeof content[field] === 'string' && content[field].trim()) {
          imageUrl = content[field];
          break;
        }
      }
    }
  } else if (type === 'job') {
    // For jobs, check company logo and other images
    if (content.hiringOrganization?.logo) {
      imageUrl = content.hiringOrganization.logo;
    } else if (content.logo) {
      imageUrl = content.logo;
    } else if (content.images && Array.isArray(content.images) && content.images.length > 0) {
      imageUrl = content.images[0];
    }
  }
  
  // If no image found, use fallback
  if (!imageUrl) {
    console.log('No image found, using fallback');
    return getFallbackImage(type);
  }
  
  // Convert to absolute URL if needed
  const absoluteImageUrl = getSocialImageUrl(imageUrl);
  console.log('Selected image URL:', absoluteImageUrl);
  
  return absoluteImageUrl;
};

/**
 * Creates a dynamic Open Graph image URL with proper dimensions
 * @param {string} imageUrl - Base image URL
 * @param {string} title - Content title
 * @param {string} type - Content type
 * @returns {string} Optimized Open Graph image URL
 */
export const createDynamicOGImage = (imageUrl, title, type = 'blog') => {
  // For now, return the image URL as is
  // In the future, you could integrate with a service like Cloudinary or similar
  // to generate dynamic OG images with text overlays
  
  const baseUrl = getSocialImageUrl(imageUrl);
  
  // Example of how you could integrate with an image generation service:
  // return `https://your-image-service.com/og-image?url=${encodeURIComponent(baseUrl)}&title=${encodeURIComponent(title)}&type=${type}`;
  
  return baseUrl;
};

/**
 * Validates and optimizes image for Open Graph sharing
 * @param {string} imageUrl - Original image URL
 * @param {string} type - Content type
 * @returns {Promise<string>} Validated and optimized image URL
 */
export const validateAndOptimizeImage = async (imageUrl, type = 'blog') => {
  if (!imageUrl) {
    return getFallbackImage(type);
  }
  
  try {
    // For now, just return the image URL
    // In a production environment, you might want to:
    // 1. Validate the image exists
    // 2. Check image dimensions
    // 3. Optimize image size
    // 4. Convert to WebP if needed
    
    const absoluteUrl = getSocialImageUrl(imageUrl);
    return absoluteUrl;
  } catch (error) {
    console.warn('Image validation failed:', error);
    return getFallbackImage(type);
  }
}; 